<template>
    <div class="auth-step">
        <button type="button" class="auth-step__back" @click="$emit('back')">
            <v-icon icon="mdi-arrow-left" size="18" class="auth-step__back-icon" />
            Back
        </button>

        <h1 class="app-typography-title-1 auth-step__title">Forgot Password</h1>
        <p class="auth-step__subtitle">
            Enter your email address and we'll send you a link to reset your password.
        </p>

        <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mb-4 rounded-lg">
            {{ errorMsg }}
        </v-alert>

        <v-alert v-if="successMsg" type="success" variant="tonal" density="compact" class="mb-4 rounded-lg">
            {{ successMsg }}
        </v-alert>

        <v-form v-if="!successMsg" ref="formRef" @submit.prevent="submitForgotPassword">
            <label class="auth-step__label">Email</label>
            <v-text-field
                v-model="email"
                placeholder="Enter your email"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                class="auth-step__field mb-6"
                :rules="[rules.required, rules.email]"
            />

            <v-btn
                block
                color="primary"
                class="text-none app-btn-primary"
                type="submit"
                :loading="isLoading"
                elevation="0"
            >
                Send Reset Link
            </v-btn>
        </v-form>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useAuthStore } from '~/stores/useAuthStore'

defineEmits<{
    back: []
}>()

const authStore = useAuthStore()
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null)
const email = ref('')
const isLoading = ref(false)
const errorMsg = ref('')
const successMsg = ref('')

const rules = {
    required: (v: string) => !!v?.trim() || 'Email is required',
    email: (v: string) => /.+@.+\..+/.test(v) || 'Email must be valid',
}

async function submitForgotPassword() {
    const { valid } = await formRef.value!.validate()
    if (!valid) return

    errorMsg.value = ''
    successMsg.value = ''
    isLoading.value = true

    const result = await authStore.forgotPassword(email.value)

    if (result.success) {
        successMsg.value = result.message || 'If the email exists in our system, a reset link will be sent.'
    } else {
        errorMsg.value = result.message || 'An error occurred while attempting to send the reset link.'
    }

    isLoading.value = false
}
</script>

<style scoped>
.auth-step__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0 0 20px;
    padding: 0;
    border: none;
    background: transparent;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
}

.auth-step__back-icon {
    color: rgb(var(--v-theme-primary));
}

.auth-step__title {
    margin: 0 0 8px;
}

.auth-step__subtitle {
    margin: 0 0 24px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: rgb(var(--v-theme-neutral));
}

.auth-step__label {
    display: block;
    margin-bottom: 8px;
    font-family: Inter, sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: rgb(var(--v-theme-neutral));
}

.auth-step__field :deep(.v-field) {
    border-radius: 8px;
}
</style>
